import React, { useEffect, useState } from 'react';
import { Button, Radio, Checkbox, Tag, Icon } from '@grofers/dock-design-kit';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';
import ConfirmationModal from './ConfirmationModal';
import ServiceDenial from './ServiceDenial';
import { MetricsForJumboEvent } from '../../../../analytics/constants';
import {
  Step4ResolutionTypeSelectedEvent,
  Step4PickupToggledEvent,
} from '../../../../analytics/copilot/events';
import { isSameRemedy } from '../utils';

const GUIDANCE_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

const CHOSEN_RESOLUTION = {
  RECOMMENDED: 'recommended',
  ALTERNATE: 'alternate',
};

/**
 * One item row inside a resolution bucket (recommended/alternate) - image,
 * name, qty x price, the per-item resolution type tag (replacement/refund -
 * items within the same bucket can differ), and a per-item "With Pickup"
 * toggle (defaults from `item.pickupDefaultSelected`, but the agent's final
 * choice - not the backend's default - is what gets submitted).
 */
const ResolutionItemRow = ({ item, pickup, onTogglePickup }) => (
  <div className='tw-w-full tw-rounded-[8px] tw-overflow-hidden tw-border tw-border-solid tw-border-[#f3f4f6] tw-bg-white tw-p-[6px] tw-flex tw-gap-2 tw-items-start'>
    <div className='tw-size-[84px] tw-rounded-lg tw-bg-[#f3f4f6] tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-overflow-hidden'>
      {item.imageUrl && (
        <img
          src={item.imageUrl}
          alt={item.name}
          className='tw-size-full tw-object-cover'
        />
      )}
    </div>
    <div className='tw-flex tw-flex-1 tw-min-w-0 tw-flex-col tw-justify-between tw-self-stretch'>
      <div className='tw-flex tw-items-start tw-justify-between tw-gap-2'>
        <div className='tw-flex tw-flex-col tw-gap-[6px] tw-min-w-0'>
          <p className='tw-text-[11px] tw-font-medium tw-text-grey-900 tw-m-0 tw-leading-[14px]'>
            {item.name}
          </p>
          <p className='tw-text-[10px] tw-text-[#384250] tw-m-0 tw-leading-[14px]'>
            {item.quantityLabel} x{item.quantity}
            {item.unitCostText ? ` | ${item.unitCostText}` : ''}
          </p>
        </div>
        <Tag
          className='!tw-text-yellow-700'
          color='yellow'
          label={item.displayText}
          type='icon'
          variant='secondary'
          size='sm'
        />
      </div>
      {item.pickupEligible && (
        <div className='tw-flex tw-items-center tw-gap-2 tw-mt-1'>
          <Checkbox size='sm' isSelected={pickup} onChange={onTogglePickup} />
          <p className='tw-text-[10px] tw-text-[#111927] tw-font-regular tw-m-0'>
            With Pickup
          </p>
        </div>
      )}
    </div>
  </div>
);

const SourceOptions = ({ options, selected, onSelect }) => (
  <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
    {options.map((option) => (
      <div key={option.value} className='tw-flex tw-items-center tw-gap-[6px]'>
        <Radio
          size='sm'
          isSelected={selected === option.value}
          onChange={() => onSelect(option.value)}
        />
        <p className='tw-text-[14px] tw-leading-[18px] tw-text-[#111927] tw-m-0'>
          {option.label}
        </p>
      </div>
    ))}
  </div>
);

// The agent's pick, else the sole option if there is only one.
const selectSource = (options, picked) => {
  if (options.some((option) => option.value === picked)) return picked;
  return options.length === 1 ? options[0].value : null;
};

/**
 * Visibility-only - the items a verified grievance level resolved to a
 * policy denial for. Never feeds back into `/resolve`'s payload (see
 * `handleConfirmResolution` below, which only submits `chosenItems`).
 * Shown separated from recommended/alternate by a divider, whether or not
 * any non-denied items remain (see `isServiceDenied` branch above).
 */
export const DeniedItemsList = ({ items = [] }) => {
  if (items.length === 0) return null;
  return (
    <div className='tw-flex tw-flex-col tw-gap-[10px] tw-w-full'>
      <div className='tw-w-full tw-border-t tw-border-solid tw-border-grey-200' />
      <p className='tw-text-[14px] tw-leading-[18px] tw-font-semibold tw-text-[#111927] tw-m-0'>
        Denied Items
      </p>
      <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
        {items.map((item) => (
          <div
            key={item.id}
            className='tw-w-full tw-rounded-[8px] tw-overflow-hidden tw-border tw-border-solid tw-border-[#f3f4f6] tw-bg-white tw-p-[6px] tw-flex tw-gap-2 tw-items-start'
          >
            <div className='tw-size-[84px] tw-rounded-lg tw-bg-[#f3f4f6] tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-overflow-hidden'>
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className='tw-size-full tw-object-cover'
                />
              )}
            </div>
            <div className='tw-flex tw-flex-1 tw-min-w-0 tw-flex-col tw-justify-between tw-self-stretch'>
              <div className='tw-flex tw-items-start tw-justify-between tw-gap-2'>
                <div className='tw-flex tw-flex-col tw-gap-[6px] tw-min-w-0'>
                  <p className='tw-text-[11px] tw-font-medium tw-text-grey-900 tw-m-0 tw-leading-[14px]'>
                    {item.name}
                  </p>
                  <p className='tw-text-[10px] tw-text-[#384250] tw-m-0 tw-leading-[14px]'>
                    {item.quantityLabel} x{item.quantity}
                  </p>
                </div>
                <Tag
                  className='!tw-text-red-700'
                  color='red'
                  label={item.reasonCode}
                  type='icon'
                  variant='secondary'
                  size='sm'
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Content of accordion step 4 - "Resolution". Phase 1 supports exactly one
 * complaint per order (see step 2/3), but resolution is decided per item -
 * within the chosen bucket (recommended or alternate), different items can
 * carry different resolution types (e.g. one item replaced, another
 * refunded), so each item has its own tag and pickup toggle rather than one
 * shared per bucket.
 *
 * `recommendedItems`/`alternateItems`/`recommendedMeta`/`alternateMeta` come
 * from `evaluate_resolution`'s poll (see CopilotView.js); `guidanceStatus`
 * drives the loading/failed gate the same way steps 2/3 do.
 */
const Resolution = ({
  recommendedItems = [],
  alternateItems = [],
  recommendedMeta = { options: [] },
  alternateMeta = { available: false, description: '', options: [] },
  deniedItems = [],
  guidanceStatus = null,
  isServiceDenied = false,
  onComplete = () => {},
  orderId = null,
  cartId = null,
  customerId = null,
}) => {
  const [chosenResolution, setChosenResolution] = useState(
    CHOSEN_RESOLUTION.RECOMMENDED,
  );
  const [recommendedOption, setRecommendedOption] = useState(null);
  const [alternateOption, setAlternateOption] = useState(null);
  const [pickupByKey, setPickupByKey] = useState({});
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // `recommendedItems`/`alternateItems` arrive asynchronously from the
  // `evaluate_resolution` poll - this component mounts as soon as step 4
  // opens, before that data exists, so a `useState` lazy initializer would
  // lock in `{}` from the first render and never see the real items once
  // the poll completes. Seed each newly-seen bucket-item key's default from
  // `pickupDefaultSelected` without touching any key the agent has already
  // toggled, same pattern as CopilotView's `productCounts` seeding.
  useEffect(() => {
    const allItems = [...recommendedItems, ...alternateItems];
    if (allItems.length === 0) return;
    setPickupByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      allItems.forEach((item, index) => {
        // Keyed by bucket, not just item id - the same item can appear in
        // both buckets with a different default pickup state.
        const bucket =
          index < recommendedItems.length ? 'recommended' : 'alternate';
        const key = `${bucket}-${item.id}`;
        if (!(key in next)) {
          next[key] = item.pickupDefaultSelected;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedItems, alternateItems]);

  if (guidanceStatus === GUIDANCE_STATUS.PENDING || !guidanceStatus) {
    return (
      <Skeleton
        className='tw-gap-2'
        rows={[
          { cols: 1, height: '4rem' },
          { cols: 1, height: '8rem' },
        ]}
      />
    );
  }

  if (guidanceStatus === GUIDANCE_STATUS.FAILED) {
    return (
      <div className='tw-w-full tw-rounded-[12px] tw-bg-[#fef3f2] tw-p-[12px]'>
        <p className='tw-text-[12px] tw-font-regular tw-text-red-500 tw-m-0'>
          Could not evaluate the resolution. Please try again.
        </p>
      </div>
    );
  }

  // Resolution-service found no viable remedy for this complaint - hand
  // off to the simpler, standalone "no service" confirmation UI instead.
  if (isServiceDenied) {
    return <ServiceDenial onComplete={onComplete} deniedItems={deniedItems} />;
  }

  const handleChooseResolution = (resolutionType) => {
    if (resolutionType === chosenResolution) return;
    setChosenResolution(resolutionType);
    Step4ResolutionTypeSelectedEvent({
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.cart_id]: cartId,
      [MetricsForJumboEvent.customer_id]: customerId,
      [MetricsForJumboEvent.feature_meta]: {
        chosen_resolution: resolutionType,
      },
    });
  };

  const togglePickup = (bucket, itemId, isSelected) => {
    setPickupByKey((prev) => ({
      ...prev,
      [`${bucket}-${itemId}`]: isSelected,
    }));
    Step4PickupToggledEvent({
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.cart_id]: cartId,
      [MetricsForJumboEvent.customer_id]: customerId,
      [MetricsForJumboEvent.feature_meta]: {
        bucket,
        item_id: itemId,
        pickup_selected: isSelected,
      },
    });
  };

  const chosenItems =
    chosenResolution === CHOSEN_RESOLUTION.RECOMMENDED
      ? recommendedItems
      : alternateItems;

  const isRecommendedChosen =
    chosenResolution === CHOSEN_RESOLUTION.RECOMMENDED;
  const recommendedSource = selectSource(
    recommendedMeta.options,
    recommendedOption,
  );
  const alternateSource = selectSource(alternateMeta.options, alternateOption);
  const refundSource =
    (isRecommendedChosen ? recommendedSource : alternateSource) ?? null;
  const chosenOptions = isRecommendedChosen
    ? recommendedMeta.options
    : alternateMeta.options;
  const canInitiate =
    chosenItems.length > 0 && (chosenOptions.length === 0 || !!refundSource);

  // The recommended card now carries the source choice, so skip an alternate
  // that just repeats it.
  const showAlternate =
    alternateMeta.available &&
    !(
      recommendedMeta.options.length > 0 &&
      isSameRemedy(recommendedItems, alternateItems)
    );

  const handleInitiateResolution = () => {
    if (!canInitiate) return;
    setIsConfirmOpen(true);
  };

  const handleConfirmResolution = () => {
    setIsConfirmOpen(false);
    onComplete({
      chosenResolution,
      refundSource,
      items: chosenItems.map((item) => ({
        id: item.id,
        productId: item.productId,
        quantity: item.quantity,
        resolutionType: item.resolutionType,
        pickup: pickupByKey[`${chosenResolution}-${item.id}`] ?? false,
      })),
    });
  };

  return (
    <div className='tw-flex tw-flex-col tw-gap-[16px] tw-w-full tw-mt-4'>
      <p className='tw-flex tw-flex-wrap tw-gap-x-1 tw-text-[12px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
        <span>Proceed with the</span>
        <span className='tw-font-semibold tw-text-[#5925dc]'>
          Recommended Resolution
        </span>
        <span>and send the Recommended Response given below</span>
      </p>

      <div className='tw-relative tw-flex tw-flex-col tw-gap-[10px] tw-w-full tw-rounded-[8px] tw-bg-[#f4f3ff] tw-p-[12px]'>
        <Icon
          name='ai-1'
          size='2xl'
          className='tw-absolute tw-top-[7px] tw-right-[10px] tw-text-[#5925dc] tw-opacity-10'
        />
        <div className='tw-flex tw-items-center tw-gap-[6px]'>
          <Radio
            size='sm'
            isSelected={chosenResolution === CHOSEN_RESOLUTION.RECOMMENDED}
            onChange={() =>
              handleChooseResolution(CHOSEN_RESOLUTION.RECOMMENDED)
            }
          />
          <p className='tw-text-[14px] tw-leading-[18px] tw-font-semibold tw-text-[#5925dc] tw-m-0'>
            Recommended Resolution
          </p>
        </div>

        {chosenResolution === CHOSEN_RESOLUTION.RECOMMENDED && (
          <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
            {recommendedItems.map((item) => (
              <ResolutionItemRow
                key={item.id}
                item={item}
                pickup={pickupByKey[`recommended-${item.id}`] ?? false}
                onTogglePickup={(isSelected) =>
                  togglePickup('recommended', item.id, isSelected)
                }
              />
            ))}
            {recommendedMeta.options.length > 0 && (
              <SourceOptions
                options={recommendedMeta.options}
                selected={recommendedSource}
                onSelect={setRecommendedOption}
              />
            )}
          </div>
        )}
      </div>

      {showAlternate && (
        <div className='tw-flex tw-flex-col tw-gap-[10px] tw-w-full tw-rounded-[6px] tw-p-[12px]'>
          <div className='tw-flex tw-items-start tw-gap-[6px]'>
            <Radio
              size='sm'
              isSelected={chosenResolution === CHOSEN_RESOLUTION.ALTERNATE}
              onChange={() =>
                handleChooseResolution(CHOSEN_RESOLUTION.ALTERNATE)
              }
            />
            <div className='tw-flex tw-flex-col tw-gap-[1px]'>
              <p className='tw-text-[14px] tw-leading-[18px] tw-font-semibold tw-text-[#111927] tw-m-0'>
                Alternate Resolution
              </p>
              <p className='tw-text-[12px] tw-leading-[16px] tw-text-[#6c737f] tw-m-0'>
                Only select this if the customer requests an alternative
                resolution
              </p>
            </div>
          </div>

          {chosenResolution === CHOSEN_RESOLUTION.ALTERNATE && (
            <div className='tw-flex tw-flex-col tw-gap-[12px] tw-w-full tw-pl-[24px]'>
              <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
                {alternateItems.map((item) => (
                  <ResolutionItemRow
                    key={item.id}
                    item={item}
                    pickup={pickupByKey[`alternate-${item.id}`] ?? false}
                    onTogglePickup={(isSelected) =>
                      togglePickup('alternate', item.id, isSelected)
                    }
                  />
                ))}
              </div>

              {alternateMeta.options.length > 0 && (
                <SourceOptions
                  options={alternateMeta.options}
                  selected={alternateSource}
                  onSelect={setAlternateOption}
                />
              )}
            </div>
          )}
        </div>
      )}

      <DeniedItemsList items={deniedItems} />

      <div className='tw-flex tw-justify-end tw-w-full'>
        <Button
          variant='primary'
          buttonType='default'
          size='md'
          shape='square'
          disabled={!canInitiate}
          onClick={handleInitiateResolution}
          className={canInitiate ? '!tw-bg-base-green' : ''}
        >
          Initiate Resolution
        </Button>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        message={
          <div>
            <p className='tw-flex tw-flex-wrap tw-gap-x-1 tw-text-[14px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
              Are you sure you want to proceed with this resolution?
              <span className='tw-font-semibold tw-text-grey-900 tw-mt-1 tw-text-[15px]'>
                No further changes can be made after this
              </span>
            </p>
          </div>
        }
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmResolution}
      />
    </div>
  );
};

export default Resolution;
